"use client";

import React from "react";
import { Network, Server, ShieldCheck, Video, Mail, Globe, Siren, Headset, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  title: "Δικτυακές Υποδομές",
  description: "Σχεδιασμός και υλοποίηση ασφαλών τοπικών δικτύων και WiFi 6 για μέγιστη ταχύτητα.",
  icon: Network,
  fact: "VPN & WiFi 6",
  link: "/ServicesPage#networks"
}, {
  title: "Διαχείριση Servers",
  description: "Παραμετροποίηση Windows Servers και συστημάτων NAS για ασφαλή αποθήκευση δεδομένων.",
  icon: Server,
  fact: "NAS & Backup",
  link: "/ServicesPage#servers"
}, {
  title: "Έλεγχος Πρόσβασης",
  description: "Συστήματα Access Control με RFID και βιομετρικά στοιχεία για απόλυτο έλεγχο κτιρίων.",
  icon: ShieldCheck,
  fact: "RFID Access",
  link: "/ServicesPage#access-control"
}, {
  title: "Συστήματα CCTV",
  description: "Εγκατάσταση έξυπνων καμερών με ανίχνευση κίνησης και απομακρυσμένη παρακολούθηση.",
  icon: Video,
  fact: "AI Detection",
  link: "/ServicesPage#cameras"
}, {
  title: "Εταιρικό Email",
  description: "Μετάβαση σε Microsoft 365 ή Google Workspace με προηγμένη προστασία από phishing.",
  icon: Mail,
  fact: "Anti-Phishing",
  link: "/ServicesPage#email"
}, {
  title: "Web Development",
  description: "Σχεδιασμός σύγχρονων ιστοσελίδων που αναδεικνύουν την επαγγελματική σας ταυτότητα.",
  icon: Globe,
  fact: "Fast & SEO",
  link: "/ServicesPage#websites"
}, {
  title: "Έξυπνοι Συναγερμοί",
  description: "Ολοκληρωμένες λύσεις ασφαλείας με άμεση ειδοποίηση στο κινητό σας για κάθε συμβάν.",
  icon: Siren,
  fact: "Mobile Alert",
  link: "/ServicesPage#alarms"
}, {
  title: "Τεχνική Υποστήριξη",
  description: "Άμεση επίλυση προβλημάτων IT μέσω απομακρυσμένης σύνδεσης ή επίσκεψης στο χώρο σας.",
  icon: Headset,
  fact: "Remote Fix",
  link: "/ServicesPage#support"
}];
export default function SupportPillars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="42117" id="εξειδικευμένες-λύσεις-πληροφορικής" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[128px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tighter uppercase font-sans">Εξειδικευμενες Λυσεις <span className="text-primary" data-ast-id="f:/src/pages/Services/sections/SupportPillars.tsx|sig:p8yc2l" data-ast-start="2925" data-ast-end="2975">Πληροφορικης</span></h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Στην <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services" className="h-7 inline-block align-middle" /> παρέχουμε την τεχνολογική ραχοκοκαλιά που χρειάζεται κάθε σύγχρονο γραφείο για να λειτουργεί με ασφάλεια, ταχύτητα και αξιοπιστία.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {services.map((service, i) => <Link key={i} to={service.link} className="group relative block h-full" data-index={i}>
              <div className="h-full relative overflow-hidden rounded-2xl border border-white/10 bg-card/40 p-6 backdrop-blur-xl transition-all duration-500 group-hover:scale-[1.02] group-hover:bg-card/60 group-hover:border-primary/50 group-hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.3)]">
                {}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                <div className="relative z-10 flex flex-col h-full">
                  {}
                  <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground shadow-sm">
                    <service.icon className="h-7 w-7" />
                  </div>

                  {}
                  <h3 className="text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors font-sans tracking-tight">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                    {service.description}
                  </p>

                  {}
                  <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-primary/80 uppercase tracking-widest font-mono opacity-0 -translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
                      {service.fact}
                    </span>
                    <ArrowRight className="h-5 w-5 text-muted-foreground opacity-50 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-primary" />
                  </div>
                </div>
              </div>
            </Link>)}
        </div>
      </div>
    </section>;
}